import { useCallback, useEffect, useState } from "react";

/**
 * Cookie consent contract (shared with PostHogProvider — do not change
 * shape without updating the provider too):
 *
 *   localStorage['cc-consent'] = JSON.stringify({ analytics, ads })
 *
 * When the user changes their choice we dispatch a `window` event named
 * `cc-consent-updated`. The PostHog provider listens to it and bootstraps
 * PostHog the moment analytics flips to `true` without a page reload.
 */

export type ConsentState = {
  analytics: boolean;
  ads: boolean;
};

const STORAGE_KEY = "cc-consent";
const UPDATE_EVENT = "cc-consent-updated";
const OPEN_PREFS_EVENT = "cc-consent-open-preferences";
const LEGACY_EVENT = "cookieConsentGiven"; // kept for backwards compat

function readStoredConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    return {
      analytics: parsed.analytics === true,
      ads: parsed.ads === true,
    };
  } catch {
    return null;
  }
}

function writeStoredConsent(next: ConsentState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage disabled (sandboxed iframe, private mode, quota). We can't
    // persist — but for the current session React state still works, so
    // don't throw; PostHog simply won't survive a reload.
  }
  window.dispatchEvent(new Event(UPDATE_EVENT));
  window.dispatchEvent(new Event(LEGACY_EVENT));
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<ConsentState | null>(() =>
    typeof window === "undefined" ? null : readStoredConsent()
  );
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  // Keep in sync if another tab writes to localStorage.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setConsent(readStoredConsent());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // `isPreferencesOpen` lives in local useState per hook instance, so the
  // footer's call to openPreferences() cannot directly toggle the modal
  // rendered inside <CookieBanner />. Bridge them via a window event that
  // every instance listens to.
  useEffect(() => {
    const onOpen = () => setIsPreferencesOpen(true);
    window.addEventListener(OPEN_PREFS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, onOpen);
  }, []);

  const acceptAll = useCallback(() => {
    const next: ConsentState = { analytics: true, ads: true };
    writeStoredConsent(next);
    setConsent(next);
    setIsPreferencesOpen(false);
  }, []);

  const rejectAll = useCallback(() => {
    const next: ConsentState = { analytics: false, ads: false };
    writeStoredConsent(next);
    setConsent(next);
    setIsPreferencesOpen(false);
  }, []);

  const savePreferences = useCallback((next: ConsentState) => {
    writeStoredConsent(next);
    setConsent(next);
    setIsPreferencesOpen(false);
  }, []);

  const openPreferences = useCallback(() => {
    setIsPreferencesOpen(true);
    window.dispatchEvent(new Event(OPEN_PREFS_EVENT));
  }, []);
  const closePreferences = useCallback(() => setIsPreferencesOpen(false), []);

  return {
    consent,
    hasDecided: consent !== null,
    isPreferencesOpen,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
  };
}
